import { useState, useMemo } from "react";
import { C, S } from "../customers/theme";
import { computeTotals } from "../lib/invoices";                 // reused, read-only (totals come from the data layer)
import { createCreditNote, CREDIT_REASONS } from "../lib/creditNotes";
import { todayISO } from "../lib/dates";

const money = (n, cur = "€") => `${cur}${(Number(n) || 0).toFixed(2)}`;
const clamp = (v, min, max) => Math.max(min, Math.min(max, Number(v) || 0));

// Create-from-invoice: pre-fills from an issued invoice's lines. Saves a DRAFT
// (createCreditNote); issuing is a separate explicit action in CreditNoteView.
export default function CreditNoteForm({ invoice, existingCredits = [], onClose, onSaved }) {
  const invLines = useMemo(() => invoice.invoice_lines || [], [invoice]);
  const cur = invoice.preferred_currency === "GBP" ? "£" : invoice.preferred_currency === "USD" ? "$" : "€";

  const [sel, setSel] = useState(() =>
    Object.fromEntries(invLines.map((l) => [l.id, { selected: false, qty: Number(l.quantity) || 0, restock: false }])));
  const [reason, setReason] = useState("return");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const setLine = (id, patch) => setSel((p) => ({ ...p, [id]: { ...p[id], ...patch } }));
  const toggleAll = (checked) => setSel((p) => Object.fromEntries(Object.entries(p).map(([id, st]) => [id, { ...st, selected: checked }])));
  const allSelected = invLines.length > 0 && invLines.every((l) => sel[l.id]?.selected);

  // Already-issued credits against this invoice → remaining creditable (UI assist; DB is the backstop).
  const alreadyCredited = existingCredits.filter((c) => c.status === "issued").reduce((s, c) => s + (Number(c.total_amount) || 0), 0);
  const remaining = Math.round((Number(invoice.total_amount || 0) - alreadyCredited) * 100) / 100;

  // Build credit lines from the selection (partial qty; DRS prorated; restock only on product lines).
  const creditLines = invLines
    .filter((l) => sel[l.id]?.selected && Number(sel[l.id]?.qty) > 0)
    .map((l) => {
      const st = sel[l.id];
      const qty = Number(st.qty) || 0;
      const invQty = Number(l.quantity) || 0;
      return {
        invoice_line_id: l.id, product_id: l.product_id, sku: l.sku, description: l.description,
        quantity: qty, unit_price: l.unit_price, discount_percent: l.discount_percent, vat_rate: l.vat_rate,
        drs: l.drs, drs_amount: l.drs && invQty > 0 ? (Number(l.drs_amount) || 0) * (qty / invQty) : 0,
        restock: !!st.restock && !!l.product_id,
      };
    });
  const totals = computeTotals(creditLines);
  const over = totals.total_amount > remaining + 0.005;

  const save = async () => {
    setBusy(true); setError("");
    try {
      await createCreditNote({
        invoice_id: invoice.id, customer_id: invoice.customer_id, customer_name: invoice.customer_name,
        issue_date: todayISO(), reason, notes, lines: creditLines,
      });
      onSaved();
    } catch (e) { setError(e.message || "Could not create credit note"); setBusy(false); }
  };

  return (
    <div style={S.modal} onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div style={{ ...S.modalBox, width: 820 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Create Credit Note</h2>
          <button onClick={onClose} aria-label="Close" style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: C.textMuted }}>✕</button>
        </div>
        <div style={{ color: C.textMuted, fontSize: 13, marginBottom: 12 }}>
          Against {invoice.invoice_number} · {invoice.customer_name || "—"} ·{" "}
          <strong style={{ color: over ? C.danger : C.text }}>remaining creditable {money(remaining, cur)}</strong>
          {alreadyCredited > 0 && <span> (already credited {money(alreadyCredited, cur)} of {money(invoice.total_amount, cur)})</span>}
        </div>

        <div style={S.grid2}>
          <div>
            <label htmlFor="cn-reason" style={S.label}>Reason</label>
            <select id="cn-reason" style={S.input} value={reason} onChange={(e) => setReason(e.target.value)}>
              {CREDIT_REASONS.map((r) => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </div>
          <div>
            <label htmlFor="cn-notes" style={S.label}>Notes</label>
            <input id="cn-notes" style={S.input} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional" />
          </div>
        </div>

        <div style={{ ...S.sectionTitle, marginTop: 14 }}>Lines to credit</div>
        <table style={S.table}>
          <thead><tr>
            <th style={{ ...S.th, width: 34 }}><input type="checkbox" aria-label="Select all lines" checked={allSelected} onChange={(e) => toggleAll(e.target.checked)} /></th>
            <th style={S.th}>Description</th>
            <th style={S.th}>Invoiced</th>
            <th style={S.th}>Credit Qty</th>
            <th style={S.th}>Unit Price</th>
            <th style={S.th}>VAT%</th>
            <th style={S.th}>Return to stock</th>
            <th style={S.th}>Line Total</th>
          </tr></thead>
          <tbody>
            {invLines.length === 0 ? <tr><td colSpan={8} style={S.empty}>This invoice has no lines.</td></tr> : invLines.map((l) => {
              const st = sel[l.id] || {};
              const invQty = Number(l.quantity) || 0;
              const lineTotal = (st.selected ? (Number(st.qty) || 0) : 0) * (Number(l.unit_price) || 0) * (1 - (Number(l.discount_percent) || 0) / 100);
              return (
                <tr key={l.id} style={{ opacity: st.selected ? 1 : 0.6 }}>
                  <td style={S.td}><input type="checkbox" aria-label={`Credit line ${l.description || l.sku || l.id}`} checked={!!st.selected} onChange={(e) => setLine(l.id, { selected: e.target.checked })} /></td>
                  <td style={S.td}>{l.description || "—"}<div style={{ color: C.textDim, fontSize: 11 }}>{l.sku || (l.product_id ? "" : "service")}</div></td>
                  <td style={S.td}>{invQty}</td>
                  <td style={S.td}>
                    <input type="number" step="0.001" min={0} max={invQty} disabled={!st.selected} value={st.qty ?? 0}
                      onChange={(e) => setLine(l.id, { qty: clamp(e.target.value, 0, invQty) })}
                      aria-label={`Credit quantity for ${l.description || l.sku || l.id}`}
                      style={{ ...S.input, width: 90, padding: "4px 8px", opacity: st.selected ? 1 : 0.5 }} />
                  </td>
                  <td style={S.td}>{money(l.unit_price, cur)}</td>
                  <td style={S.td}>{Number(l.vat_rate) || 0}</td>
                  <td style={S.td}>
                    {l.product_id
                      ? <label style={{ display: "inline-flex", alignItems: "center", gap: 6, cursor: st.selected ? "pointer" : "default", color: st.selected ? C.text : C.textDim, fontSize: 12.5 }}>
                          <input type="checkbox" disabled={!st.selected} checked={!!st.restock} onChange={(e) => setLine(l.id, { restock: e.target.checked })} aria-label={`Return ${l.description || l.sku} to stock`} /> restock
                        </label>
                      : <span style={{ color: C.textDim, fontSize: 12 }}>service — n/a</span>}
                  </td>
                  <td style={S.td}>{money(lineTotal, cur)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>

        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginTop: 12, gap: 16, flexWrap: "wrap" }}>
          <div style={{ fontSize: 12.5 }}>
            {over && <div style={{ color: C.danger, fontWeight: 700 }}>⚠ This credit ({money(totals.total_amount, cur)}) exceeds the remaining creditable ({money(remaining, cur)}). Reduce it before saving.</div>}
            {!over && creditLines.length > 0 && <div style={{ color: C.textMuted }}>Saving creates a <strong>draft</strong> — you issue it separately.</div>}
          </div>
          <div style={{ background: C.surface2, borderRadius: 8, padding: 14, minWidth: 240 }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}><span style={{ color: C.textMuted }}>Subtotal</span><strong>{money(totals.subtotal, cur)}</strong></div>
            {(totals.vat_breakdown || []).map((b) => (
              <div key={b.rate} style={{ display: "flex", justifyContent: "space-between", marginBottom: 4, fontSize: 12.5 }}><span style={{ color: C.textMuted }}>VAT @ {b.rate}%</span><span>{money(b.vat, cur)}</span></div>
            ))}
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}><span style={{ color: C.textMuted }}>Total VAT</span><strong>{money(totals.total_vat, cur)}</strong></div>
            {totals.total_drs > 0 && <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}><span style={{ color: C.textMuted }}>DRS deposit</span><strong>{money(totals.total_drs, cur)}</strong></div>}
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 16, fontWeight: 800, borderTop: `1px solid ${C.border}`, paddingTop: 8 }}><span>Credit total</span><span>{money(totals.total_amount, cur)}</span></div>
          </div>
        </div>

        {error && <div style={{ color: C.danger, fontSize: 13, fontWeight: 600, marginTop: 12 }} role="alert">{error}</div>}
        <div style={{ display: "flex", gap: 10, marginTop: 18, justifyContent: "flex-end" }}>
          <button type="button" style={S.btn("secondary")} onClick={onClose} disabled={busy}>Cancel</button>
          <button type="button" style={{ ...S.btn(), opacity: (busy || over || creditLines.length === 0) ? 0.5 : 1 }} disabled={busy || over || creditLines.length === 0} onClick={save}>{busy ? "Saving…" : "Save draft credit note"}</button>
        </div>
      </div>
    </div>
  );
}
